import React, { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

// Configurações da API - ATUALIZADAS para o endpoint PHP
const API_CONFIG = {
  baseURL: 'https://api.coopdelta.com.br',
  token: '1234',
  endpoints: {
    // Endpoint PHP para dados dos sensores
    sensorData: '/sensor-data.php',           // GET/POST dados dos sensores
    healthCheck: '/sensor-data.php'           // Usando o mesmo endpoint para health check
  }
};

// Componente para medidor circular
const CircularGauge = ({ value, min, max, unit, label, color = '#3B82F6', dangerZone = null }) => {
  const percentage = Math.min(Math.max(((value - min) / (max - min)) * 100, 0), 100);
  const circumference = 2 * Math.PI * 45;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  // Determinar cor baseada na zona de perigo
  let gaugeColor = color;
  if (dangerZone && value !== undefined && value !== null) {
    if (value < dangerZone.min || value > dangerZone.max) {
      gaugeColor = '#EF4444'; // Vermelho para valores perigosos
    } else if (value < dangerZone.warning.min || value > dangerZone.warning.max) {
      gaugeColor = '#F59E0B'; // Amarelo para valores de atenção
    } else {
      gaugeColor = '#10B981'; // Verde para valores normais
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6 flex flex-col items-center">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{label}</h3>
      
      <div className="relative w-32 h-32">
        <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 100 100">
          <circle
            cx="50"
            cy="50"
            r="45"
            stroke="#E5E7EB"
            strokeWidth="8"
            fill="none"
          />
          <circle
            cx="50"
            cy="50"
            r="45"
            stroke={gaugeColor}
            strokeWidth="8"
            fill="none"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold text-gray-800">
            {value !== undefined && value !== null ? value.toFixed(1) : '--'}
          </span>
          <span className="text-sm text-gray-500">{unit}</span>
        </div>
      </div>
      
      <div className="mt-4 text-center">
        <div className="text-xs text-gray-500">
          Min: {min} | Max: {max}
        </div>
        {dangerZone && (
          <div className="text-xs mt-1">
            <span className="text-green-600">Ideal: {dangerZone.warning.min}-{dangerZone.warning.max}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Componente para cartão de informação simples
const InfoCard = ({ value, unit, label, icon, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-800',
    green: 'bg-green-100 text-green-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    red: 'bg-red-100 text-red-800'
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center">
        <div className={`p-3 rounded-full ${colorClasses[color]}`}>
          {icon}
        </div>
        <div className="ml-4">
          <h3 className="text-lg font-semibold text-gray-800">{label}</h3>
          <p className="text-2xl font-bold text-gray-900">
            {value} <span className="text-sm font-normal text-gray-500">{unit}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

// Componente principal da página
const WaterMonitoring = () => {
  const [waterData, setWaterData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('checking');

  // ATUALIZADO: Função para buscar dados do endpoint PHP
  const fetchWaterData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Buscar dados do endpoint PHP
      const response = await fetch(
        `${API_CONFIG.baseURL}${API_CONFIG.endpoints.sensorData}?token=${API_CONFIG.token}&action=latest`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${API_CONFIG.token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Dados recebidos da API PHP:', data);
      
      // Mapear dados com verificações robustas
      const mappedData = {
        temp: parseFloat(data.temperatura) || parseFloat(data.temp) || 0,
        ph: parseFloat(data.ph) || 0,
        tds: parseFloat(data.tds) || 0,
        cond: parseFloat(data.condutividade) || parseFloat(data.cond) || 0,
        ntu: parseFloat(data.turbidez) || parseFloat(data.ntu) || 0,
        lat: data.latitude || data.lat || "-2.543210",
        lon: data.longitude || data.lon || "-44.345678",
        timestamp: data.timestamp || new Date().toISOString(),
        device_id: data.device_id || 'unknown'
      };

      setWaterData(mappedData);
      setLastUpdate(new Date());
      setConnectionStatus('connected');
      
    } catch (err) {
      console.error('Erro ao buscar dados:', err);
      setError(`Erro na conexão: ${err.message}`);
      setConnectionStatus('error');
      
      // Fallback para dados simulados se não houver dados anteriores
      if (!waterData) {
        const fallbackData = {
          temp: 26.5,
          ph: 7.24,
          tds: 456.78,
          cond: 24.56,
          ntu: 120.34,
          lat: "-2.543210",
          lon: "-44.345678",
          timestamp: new Date().toISOString(),
          device_id: 'fallback'
        };
        setWaterData(fallbackData);
      }
      
    } finally {
      setLoading(false);
    }
  };

  // ATUALIZADO: Teste de conectividade do endpoint PHP
  const testApiConnection = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.healthCheck}?token=${API_CONFIG.token}&action=ping`, {
        method: 'GET',
        signal: controller.signal,
        headers: {
          'Authorization': `Bearer ${API_CONFIG.token}`
        }
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        setConnectionStatus('connected');
        return true;
      } else {
        setConnectionStatus('error');
        return false;
      }
    } catch (err) {
      console.warn('Health check falhou:', err);
      setConnectionStatus('error');
      return false;
    }
  };

  // SIMPLIFICADO: useEffect apenas com o método principal
  useEffect(() => {
    // Teste inicial e busca de dados
    const initializeData = async () => {
      await testApiConnection();
      await fetchWaterData();
    };

    initializeData();
    
    // Intervalo de atualização a cada 30 segundos
    const interval = setInterval(() => {
      fetchWaterData();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // Parâmetros de qualidade da água para cada medição
  const waterParameters = {
    temp: {
      min: 0,
      max: 40,
      unit: '°C',
      label: 'Temperatura',
      dangerZone: {
        min: 20,
        max: 30,
        warning: { min: 22, max: 28 }
      }
    },
    ph: {
      min: 0,
      max: 14,
      unit: 'pH',
      label: 'Potencial Hidrogeniônico',
      dangerZone: {
        min: 6.0,
        max: 8.5,
        warning: { min: 6.5, max: 8.0 }
      }
    },
    tds: {
      min: 0,
      max: 1000,
      unit: 'mg/L',
      label: 'Sólidos Totais Dissolvidos',
      dangerZone: {
        min: 0,
        max: 500,
        warning: { min: 0, max: 300 }
      }
    },
    cond: {
      min: 0,
      max: 100,
      unit: 'mS/cm',
      label: 'Condutividade',
      dangerZone: {
        min: 0,
        max: 50,
        warning: { min: 0, max: 30 }
      }
    },
    ntu: {
      min: 0,
      max: 200,
      unit: 'NTU',
      label: 'Turbidez',
      dangerZone: {
        min: 0,
        max: 40,
        warning: { min: 0, max: 25 }
      }
    }
  };

  if (loading && !waterData) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-lg text-gray-600">Conectando aos sensores IoT...</p>
            <p className="text-sm text-gray-500 mt-2">
              Status: {connectionStatus === 'checking' ? 'Verificando conexão' : 
                      connectionStatus === 'connected' ? 'Conectado' : 'Tentando reconectar...'}
            </p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-8">
        {/* Cabeçalho da página */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-primary mb-2">
                Monitoramento da Qualidade da Água
              </h1>
              <p className="text-gray-600">
                Dados em tempo real do Rio Delta - Carnaubeiras, Araioses-MA
              </p>
              {waterData?.device_id && (
                <p className="text-xs text-gray-500 mt-1">
                  Dispositivo: {waterData.device_id}
                </p>
              )}
            </div>
            
            <div className="text-right">
              <div className="flex gap-2 mb-2">
                <button
                  onClick={fetchWaterData}
                  disabled={loading}
                  className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Atualizando...' : 'Atualizar Dados'}
                </button>
                
                <button
                  onClick={testApiConnection}
                  className={`px-4 py-2 rounded text-white transition-colors ${
                    connectionStatus === 'connected' ? 'bg-green-500 hover:bg-green-600' : 
                    connectionStatus === 'error' ? 'bg-red-500 hover:bg-red-600' : 'bg-yellow-500 hover:bg-yellow-600'
                  }`}
                >
                  {connectionStatus === 'connected' ? '🟢 Online' : 
                   connectionStatus === 'error' ? '🔴 Offline' : '🟡 Verificando'}
                </button>
              </div>
              
              {lastUpdate && (
                <p className="text-sm text-gray-500">
                  Última atualização: {lastUpdate.toLocaleTimeString('pt-BR')}
                </p>
              )}
              
              {error && (
                <p className="text-sm text-red-600 mt-1">
                  ⚠️ {error}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Grid principal de medidores */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {waterData && Object.entries(waterParameters).map(([key, params]) => (
            <CircularGauge
              key={key}
              value={waterData[key]}
              min={params.min}
              max={params.max}
              unit={params.unit}
              label={params.label}
              dangerZone={params.dangerZone}
            />
          ))}
        </div>

        {/* Informações de localização */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <InfoCard
            value={waterData?.lat || "-2.543210"}
            unit=""
            label="Latitude"
            icon={<span className="text-lg">📍</span>}
            color="blue"
          />
          <InfoCard
            value={waterData?.lon || "-44.345678"}
            unit=""
            label="Longitude"
            icon={<span className="text-lg">🌍</span>}
            color="green"
          />
        </div>

        {/* Status da API */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Status do Sistema
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className={`text-center p-4 rounded-lg ${
              connectionStatus === 'connected' ? 'bg-green-50' : 'bg-red-50'
            }`}>
              <div className={`text-2xl mb-2 ${
                connectionStatus === 'connected' ? 'text-green-600' : 'text-red-600'
              }`}>
                {connectionStatus === 'connected' ? '🟢' : '🔴'}
              </div>
              <h3 className={`font-semibold ${
                connectionStatus === 'connected' ? 'text-green-800' : 'text-red-800'
              }`}>
                Conexão API
              </h3>
              <p className={`text-sm ${
                connectionStatus === 'connected' ? 'text-green-600' : 'text-red-600'
              }`}>
                {connectionStatus === 'connected' ? 'Online' : 'Offline'}
              </p>
            </div>
            
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-blue-600 text-2xl mb-2">⏱️</div>
              <h3 className="font-semibold text-blue-800">Atualização</h3>
              <p className="text-sm text-blue-600">A cada 30 segundos</p>
            </div>
            
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-purple-600 text-2xl mb-2">📡</div>
              <h3 className="font-semibold text-purple-800">Sensores</h3>
              <p className="text-sm text-purple-600">5 parâmetros ativos</p>
            </div>

            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <div className="text-orange-600 text-2xl mb-2">🤖</div>
              <h3 className="font-semibold text-orange-800">IoT Device</h3>
              <p className="text-sm text-orange-600">ESP32-S3 Ativo</p>
            </div>
          </div>
        </div>

        {/* Resumo da qualidade - CORRIGIDO */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Resumo da Qualidade da Água
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-green-600 text-2xl mb-2">✓</div>
              <h3 className="font-semibold text-green-800">Parâmetros Normais</h3>
              <p className="text-sm text-green-600">
                {waterData ? Object.entries(waterParameters).filter(([key, params]) => {
                  const value = waterData[key];
                  return value !== undefined && value !== null && 
                         value >= params.dangerZone.warning.min && 
                         value <= params.dangerZone.warning.max;
                }).length : 0} de {Object.keys(waterParameters).length}
              </p>
            </div>
            
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="text-yellow-600 text-2xl mb-2">⚠</div>
              <h3 className="font-semibold text-yellow-800">Atenção</h3>
              <p className="text-sm text-yellow-600">
                {waterData ? Object.entries(waterParameters).filter(([key, params]) => {
                  const value = waterData[key];
                  return value !== undefined && value !== null && 
                         ((value >= params.dangerZone.min && value < params.dangerZone.warning.min) ||
                          (value > params.dangerZone.warning.max && value <= params.dangerZone.max));
                }).length : 0} parâmetros
              </p>
            </div>
            
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="text-red-600 text-2xl mb-2">⚡</div>
              <h3 className="font-semibold text-red-800">Crítico</h3>
              <p className="text-sm text-red-600">
                {waterData ? Object.entries(waterParameters).filter(([key, params]) => {
                  const value = waterData[key];
                  return value !== undefined && value !== null && 
                         (value < params.dangerZone.min || value > params.dangerZone.max);
                }).length : 0} parâmetros
              </p>
            </div>
          </div>
        </div>



        {/* Informações sobre os parâmetros */}
        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Sobre os Parâmetros Monitorados
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div>
              <h3 className="font-semibold mb-2">Temperatura (°C)</h3>
              <p className="text-gray-600 mb-4">
                A temperatura da água afeta a solubilidade do oxigênio e influencia o metabolismo aquático.
              </p>
              
              <h3 className="font-semibold mb-2">pH</h3>
              <p className="text-gray-600 mb-4">
                Mede a acidez ou alcalinidade da água. Valores ideais estão entre 6,5 e 8,0.
              </p>
              
              <h3 className="font-semibold mb-2">Sólidos Totais Dissolvidos (mg/L)</h3>
              <p className="text-gray-600">
                Indica a quantidade de minerais, sais e metais dissolvidos na água.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold mb-2">Condutividade (mS/cm)</h3>
              <p className="text-gray-600 mb-4">
                Mede a capacidade da água de conduzir eletricidade, relacionada aos sólidos dissolvidos.
              </p>
              
              <h3 className="font-semibold mb-2">Turbidez (NTU)</h3>
              <p className="text-gray-600 mb-4">
                Mede a clareza da água. Valores altos podem indicar poluição ou sedimentos.
              </p>
              
              <h3 className="font-semibold mb-2">Monitoramento IoT</h3>
              <p className="text-gray-600">
                Dados coletados automaticamente pelo sistema ESP32-S3 instalado no Rio Delta.
              </p>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default WaterMonitoring;